// English.
//
// ═══════════════════════════════════════════════════════════════════
// WHY THIS FILE EXISTS BEFORE THE INTERFACE IS TRANSLATED
// ═══════════════════════════════════════════════════════════════════
//
// A translation system with one language is not a translation system:
// it is a lookup table that happens to work. Nothing tells you whether
// the keys are real keys or just the Spanish text wearing a hat.
//
// This file is the test. Every key here is a value that is **written in
// the database in Spanish and stays that way forever** — `"Superior"`,
// `"Azulclaro"`, `"Camisa de vestir"`. If any of them had to change to
// support English, the whole design would be wrong. None of them did.
//
// ⚠️  THE LEFT SIDE IS A DATABASE VALUE. NEVER EDIT IT. It is validated
//     by CHECK constraints in `supabase/schema.sql`. Changing one
//     breaks every wardrobe. Only the right side is translation.
//
// ⚠️  This file covers the CATALOG only — the words that name garments,
//     colours, materials and occasions. The ~682 interface strings are
//     still Spanish everywhere. A user who picks English today gets
//     English garment names inside a Spanish interface. That is
//     deliberate: it is the half that is finished, and it is visibly
//     half rather than silently missing.
//
// ── Choices worth arguing with ──────────────────────────────────────
//
//   · `Azuloscuro` → "Navy", not "Dark blue". It is the colour people
//     actually name when they see it on clothing.
//   · `Petroleo` → "Teal". "Petrol blue" is British and narrower.
//   · `Top deportivo` → "Athletic top", not "Sports bra". The Spanish
//     does not say which, and guessing puts a garment in someone's
//     wardrobe under a name they did not choose.
//   · `Zapatilla deportiva` → "Athletic sneaker". American English;
//     British would say "trainers". Same problem Spanish has with
//     "tenis" vs "zapatilla" — there is no neutral, so pick and say so.
//   · American spelling throughout: "Gray", "Pajamas".

const en = {
  catalogo: {
    familiaColor: {
      "Negros": "Blacks",
      "Blancos": "Whites",
      "Grises": "Greys",
      "Beige": "Beiges",
      "Azules": "Blues",
      "Marrones": "Browns",
      "Verdes": "Greens",
      "Rojos": "Reds",
      "Rosas": "Pinks",
      "Violetas": "Purples",
      "Naranjas": "Oranges",
      "Amarillos": "Yellows",
    },
    subcategoria: {
      "Camisas y blusas": "Shirts & blouses",
      "Camisetas y polos": "T-shirts & polos",
      "Deporte": "Activewear",
      "Pantalones": "Trousers",
      "Faldas": "Skirts",
      "Shorts y bermudas": "Shorts",
      "Ropa de baño": "Swimwear",
      "Vestidos": "Dresses",
      "Enterizos y monos": "Jumpsuits",
      "Interior y descanso": "Underwear & loungewear",
      "Sastrería": "Tailoring",
      "Suéteres": "Sweaters",
      "Sudaderas": "Sweatshirts",
      "Chaquetas": "Jackets",
      "Abrigos": "Coats",
      "Formal e intermedio": "Formal & smart-casual",
      "Botas": "Boots",
      "Casual y deportivo": "Casual & sport",
      "Complementos de vestir": "Accessories",
      "Bolsos y equipaje": "Bags & luggage",
      "Joyería y relojería": "Jewellery & watches",
      "Otros accesorios": "Other accessories",
    },
    tipo: {
      "Superior": "Top",
      "Inferior": "Bottom",
      "Enterizo": "One-piece",
      "Calzado": "Shoes",
      "Abrigo": "Outerwear",
      "Accesorios": "Accessories",
    },
    color: {
      "Negro": "Black",
      "Blanco": "White",
      "Azul": "Blue",
      "Azulclaro": "Light blue",
      "Azuloscuro": "Navy",
      "Rojo": "Red",
      "Verde": "Green",
      "Verdeclaro": "Light green",
      "Gris": "Gray",
      "Beige": "Beige",
      "Marrón": "Brown",
      "MarrónOscuro": "Dark brown",
      "Naranja": "Orange",
      "Amarillo": "Yellow",
      "Morado": "Purple",
      "violeta": "Violet",
      "Rosa": "Pink",
      "Borgoña": "Burgundy",
      "Celeste": "Sky blue",
      "Petroleo": "Teal",
    },
    material: {
      "Algodón": "Cotton",
      "Denim": "Denim",
      "Lana": "Wool",
      "Poliéster": "Polyester",
      "Mezcla": "Blend",
      "Cuero": "Leather",
      "Gamuza": "Suede",
      "Seda": "Silk",
      "Lino": "Linen",
    },
    estado: {
      "Nueva": "New",
      "Buena": "Good",
      "Usada": "Used",
      "Gastada": "Worn out",
    },
    disponibilidad: {
      "Disponible": "Available",
      "En lavandería": "In the wash",
      "En arreglo": "Being repaired",
      "Guardada": "Stored",
    },
    ocasion: {
      "Deporte": "Sport",
      "Estar en casa/Descanso": "At home/Rest",
      "Uso diario": "Everyday",
      "Trabajo formal": "Formal work",
      "Noche/Fiestas/Clubbing": "Night out/Parties/Clubbing",
      "Corporativo formal": "Corporate formal",
      "Etiqueta/Gala": "Black tie/Gala",
      "Trabajo casual": "Smart casual work",
      "Ropa de trabajo": "Workwear",
      "Videollamada": "Video call",
      "Clases": "Classes",
      "Juntada": "Meetup",
      "Trabajo": "Work",
      "Trabajo día especial": "Work, special day",
      "Noche": "Night out",
      "Casual": "Casual",
      "Entrecasa": "At home",
      "Gym": "Gym",
      "Verano noche": "Summer night",
      "Verano casual": "Summer casual",
    },
    zona: {
      "torso": "torso",
      "piernas": "legs",
      "pies": "feet",
    },
    estacion: {
      "Primavera": "Spring",
      "Verano": "Summer",
      "Otoño": "Fall",
      "Invierno": "Winter",
    },
    preset: {
      "Primavera-Verano": "Spring–Summer",
      "Otoño-Invierno": "Fall–Winter",
      "Entretiempo": "Transitional",
      "Todo el año": "All year",
    },
    prenda: {
      "Camisa de vestir": "Dress shirt",
      "Camisa casual": "Casual shirt",
      "Camisa estampada": "Printed shirt",
      "Camisa oversize": "Oversized shirt",
      "Guayabera": "Guayabera",
      "Blusa elegante": "Dressy blouse",
      "Blusa casual": "Casual blouse",
      "Top sin mangas": "Sleeveless top",
      "Camiseta básica": "Basic tee",
      "Polo": "Polo shirt",
      "Camiseta henley": "Henley",
      "Camiseta sin mangas": "Tank top",
      "Top deportivo": "Athletic top",
      "Camiseta deportiva": "Athletic tee",
      "Pantalón de vestir": "Dress pants",
      "Pantalón chino": "Chinos",
      "Jeans": "Jeans",
      "Pantalón cargo": "Cargo pants",
      "Pantalón ancho": "Wide-leg pants",
      "Jogger": "Joggers",
      "Leggings": "Leggings",
      "Falda lápiz": "Pencil skirt",
      "Falda midi": "Midi skirt",
      "Falda larga": "Maxi skirt",
      "Falda plisada": "Pleated skirt",
      "Minifalda": "Mini skirt",
      "Bermuda de vestir": "Dress shorts",
      "Short casual": "Casual shorts",
      "Short deportivo": "Athletic shorts",
      "Mallas de compresión": "Compression tights",
      "Traje de baño tipo bermuda": "Swim trunks",
      "Vestido de noche": "Evening dress",
      "Vestido de cóctel": "Cocktail dress",
      "Vestido de día": "Day dress",
      "Vestido blazer": "Blazer dress",
      "Jumpsuit formal": "Formal jumpsuit",
      "Jumpsuit casual": "Casual jumpsuit",
      "Mono corto": "Romper",
      "Overol": "Overalls",
      "Traje de baño entero": "One-piece swimsuit",
      "Bikini": "Bikini",
      "Pijama": "Pajamas",
      "Blazer estructurado": "Structured blazer",
      "Blazer casual": "Casual blazer",
      "Chaleco de vestir": "Waistcoat",
      "Suéter cuello alto": "Turtleneck sweater",
      "Suéter cuello V o redondo": "V-neck or crew sweater",
      "Cárdigan": "Cardigan",
      "Suéter oversize": "Oversized sweater",
      "Sudadera con capucha": "Hoodie",
      "Sudadera sin capucha": "Sweatshirt",
      "Sudadera con cierre": "Zip-up sweatshirt",
      "Chaqueta de cuero": "Leather jacket",
      "Chaqueta de jeans": "Denim jacket",
      "Chaqueta bomber": "Bomber jacket",
      "Sobrecamisa": "Overshirt",
      "Abrigo de lana": "Wool coat",
      "Gabardina": "Trench coat",
      "Chaqueta acolchada": "Puffer jacket",
      "Parka": "Parka",
      "Impermeable": "Raincoat",
      "Bata": "Robe",
      "Salida de baño": "Bathrobe",
      "Zapato Oxford o Derby": "Oxford or Derby shoe",
      "Mocasín": "Loafer",
      "Zapato de tacón": "Heels",
      "Sandalia de vestir": "Dress sandal",
      "Alpargata": "Espadrille",
      "Botín Chelsea": "Chelsea boot",
      "Botín con cordones": "Lace-up ankle boot",
      "Bota de vestir": "Dress boot",
      "Bota militar": "Combat boot",
      "Bota de lluvia": "Rain boot",
      "Bota de nieve": "Snow boot",
      "Zapatilla urbana": "Casual sneaker",
      "Náutico": "Boat shoe",
      "Zapatilla deportiva": "Athletic sneaker",
      "Sandalia tipo slide": "Slide sandal",
      "Sandalia casual": "Casual sandal",
      "Corbata": "Tie",
      "Moño": "Bow tie",
      "Pañuelo de bolsillo": "Pocket square",
      "Cinturón": "Belt",
      "Tirantes": "Suspenders",
      "Bolso de mano": "Handbag",
      "Bolso cruzado": "Crossbody bag",
      "Bolso tote": "Tote bag",
      "Mochila urbana": "Backpack",
      "Maletín": "Briefcase",
      "Riñonera": "Belt bag",
      "Maleta de viaje": "Suitcase",
      "Reloj formal": "Dress watch",
      "Smartwatch": "Smartwatch",
      "Anillo": "Ring",
      "Collar": "Necklace",
      "Pulsera": "Bracelet",
      "Aretes": "Earrings",
      "Gemelos": "Cufflinks",
      "Sombrero": "Hat",
      "Gorra": "Cap",
      "Gorro de lana": "Beanie",
      "Bufanda": "Scarf",
      "Guantes": "Gloves",
      "Lentes de sol": "Sunglasses",
      "Ropa interior": "Underwear",
      "Calcetines de vestir": "Dress socks",
      "Calcetines deportivos": "Athletic socks",
      "Calcetines invisibles": "No-show socks",
    },
  },

  estilo: {
    deportivo: { nombre: "Sporty", mensaje: "Easy and effortless" },
    tradicional: { nombre: "Classic", mensaje: "Reliable and serious" },
    elegante: { nombre: "Elegant", mensaje: "Understated and refined" },
    romantico: { nombre: "Romantic", mensaje: "Soft and warm" },
    magnetico: { nombre: "Magnetic", mensaje: "Present and sensual" },
    creativo: { nombre: "Creative", mensaje: "Original and your own" },
    dramatico: { nombre: "Dramatic", mensaje: "Bold and current" },
  },

  /* Server errors, keyed by the backend code. {x} markers are
   * filled from `datos`. Same translation caveat as the catalog. */
  error: {
    ACCOUNT_DELETE_FAILED: "Your account could not be deleted. Nothing was lost — your account is still there. Please try again.",
    ACCOUNT_EXISTS: "An account with that email already exists.",
    AI_NOT_CONFIGURED: "The server has no AI API key configured.",
    AI_UNAVAILABLE: "Automatic photo analysis is unavailable right now. You can add your garments by hand and fill in the details later.",
    BATCH_FAILED: "We couldn't process the photo batch.",
    BATCH_TOO_LARGE: "Up to {max} photos per batch. You sent {enviadas}.",
    BODY_NOT_JSON: "The request body is not valid JSON.",
    BODY_TOO_LARGE: "That content is too large for this operation.",
    BOTH_GARMENTS_MISSING: "Both garments are required.",
    CHOSEN_NOT_LIST: "The chosen garments must be a list.",
    COLORS_LOAD_FAILED: "We couldn't load your colours.",
    COLOR_BAD_HEX: "The colour must be a six-digit hex value, like #C8322F.",
    COLOR_CREATE_FAILED: "We couldn't create the colour.",
    COLOR_DELETE_FAILED: "We couldn't delete the colour.",
    COLOR_EXISTS_IN_LIST: "“{nombre}” is already on the list.",
    COLOR_IN_USE: "“{nombre}” is in use by {n} garment(s).",
    COLOR_LIMIT_REACHED: "You've reached the limit of {max} custom colours.",
    COLOR_NAME_MISSING: "The colour name is missing.",
    COLOR_NOT_FOUND: "That colour doesn't exist.",
    DAILY_PHOTO_LIMIT: "You've reached today's photo limit. What you already uploaded is saved — you can continue tomorrow, or keep adding garments by hand.",
    DATE_INVALID: "That date isn't valid.",
    DATE_IN_FUTURE: "The date can't be in the future.",
    DAYS_NOT_OBJECT: "Days per occasion must be an object.",
    DAYS_PER_OCCASION_MISSING: "You need to say how many days of each occasion.",
    DUPLICATE_COLOR: "You already have a colour called “{nombre}”.",
    DUPLICATE_NAME: "Something with that name already exists.",
    DUPLICATE_OCCASION: "You already have an occasion called “{nombre}”.",
    EMAIL_INVALID: "That email doesn't look valid.",
    EMAIL_MISSING: "Email is missing.",
    EMAIL_NOT_CONFIRMED: "You need to confirm your email before signing in. Check your inbox.",
    EMAIL_PASSWORD_REQUIRED: "Email and password are required.",
    EMAIL_RATE_LIMIT: "Email limit reached. Wait a few minutes and try again.",
    EMAIL_SEND_FAILED: "We couldn't send the email. It's a problem on our side, not yours. Try again in a while.",
    FIELD_INVALID: "Invalid value in {campo}.",
    GARMENT_LIMIT_REACHED: "During the trial you can keep up to {max} garments. With a subscription there is no limit.",
    FORMALITY_MISSING: "The formality level is missing. Without it the style rule can't judge the garment.",
    GARMENTS_SAVE_FAILED: "We couldn't save the garments.",
    GARMENT_DELETE_FAILED: "We couldn't delete the garment.",
    GARMENT_EDIT_FAILED: "We couldn't edit the garment.",
    GARMENT_LIST_MISSING: "The garment list is missing.",
    GARMENT_LOAD_FAILED: "We couldn't load the garment.",
    GARMENT_NOT_FOUND: "That garment doesn't exist.",
    GARMENT_USES_FAILED: "We couldn't load when you wore this garment.",
    HISTORY_LOAD_FAILED: "We couldn't load your history.",
    IMAGE_EMPTY: "The image arrived empty.",
    INVALID_CREDENTIALS: "Wrong email or password.",
    LOGIN_FAILED: "We couldn't sign you in.",
    NOTHING_TO_CHANGE: "There's nothing to change.",
    NO_CHANGES_SENT: "You didn't send any changes.",
    NO_GARMENTS_SENT: "You didn't send any garments.",
    NO_PHOTOS_SENT: "You didn't send any photos.",
    OCCASIONS_LOAD_FAILED: "We couldn't load your occasions.",
    OCCASION_CREATE_FAILED: "We couldn't create the occasion.",
    OCCASION_DELETE_FAILED: "We couldn't delete the occasion.",
    OCCASION_EXISTS_IN_LIST: "“{nombre}” is already on the list.",
    OCCASION_IN_USE: "“{nombre}” is in use by {n} garment(s).",
    OCCASION_LEVEL_RANGE: "The level must be a whole number from {min} to {max}.",
    OCCASION_LIMIT_REACHED: "You've reached the limit of {max} custom occasions.",
    OCCASION_LIST_ALREADY_SET: "Your list is already set. You can add or remove occasions from your profile.",
    OCCASION_LIST_BUILD_FAILED: "We couldn't build your occasion list.",
    OCCASION_NAME_MISSING: "The occasion name is missing.",
    OCCASION_NOT_FOUND: "That occasion doesn't exist.",
    OPERATION_FAILED: "We couldn't complete that. Please try again.",
    ORIGIN_NOT_ALLOWED: "That origin isn't allowed.",
    PERFUME_NOT_FOUND: "That perfume doesn't exist.",
    PERFUME_NAME_MISSING: "Write the perfume's name.",
    PERFUME_FIELD_INVALID: "One of the perfume's details isn't valid ({campo}).",
    PERFUME_SAVE_FAILED: "We couldn't save the perfume.",
    PERFUME_LOAD_FAILED: "We couldn't load your perfumes.",
    PERFUME_DELETE_FAILED: "We couldn't delete the perfume.",
    PERFUME_AI_FAILED: "The AI couldn't answer right now. You can fill in the details by hand.",
    OUTFIT_EDIT_FAILED: "We couldn't update that entry.",
    OUTFIT_LOG_FAILED: "We couldn't log the outfit.",
    OUTFIT_NOT_FOUND: "That entry doesn't exist.",
    PASSWORD_NEEDS_REAUTH: "For your security, confirm your identity before changing the password. Request a new recovery link from the sign-in screen.",
    PASSWORD_CHANGE_FAILED: "We couldn't change the password.",
    PASSWORD_TOO_SHORT: "The password must be at least 8 characters.",
    PLAN_EXPIRED: "Your plan has ended. You can still see, edit and log your wardrobe — to add new garments you need a subscription.",
    PHOTO_DISCARD_FAILED: "We couldn't discard the photo.",
    PHOTO_IN_USE: "That photo belongs to a garment.",
    PHOTO_NOT_PROCESSED: "This photo was left out of the batch. Try it again on its own.",
    PHOTO_NOT_YOURS: "That photo isn't yours.",
    PHOTO_PATH_MISSING: "The photo path is missing.",
    PHOTO_UPLOAD_FAILED: "We couldn't upload the photo.",
    PROFILE_LOAD_FAILED: "We couldn't load your profile.",
    PROFILE_MISSING: "Your account exists but has no profile.",
    PROFILE_SAVE_FAILED: "We couldn't save your profile.",
    RECORD_DELETE_FAILED: "We couldn't delete that entry.",
    REFERENCE_MISSING: "That refers to something which no longer exists.",
    REFRESH_TOKEN_MISSING: "The refresh token is missing.",
    REQUIRED_FIELD_MISSING: "A required field is missing.",
    SAME_GARMENT: "Those are the same garment.",
    SAVE_FAILED: "We couldn't save that.",
    SERVER_ERROR: "Something went wrong on our end.",
    SESSION_INVALID: "Your session is invalid or has expired.",
    SESSION_REFRESH_FAILED: "We couldn't refresh your session.",
    SESSION_TOKEN_MISSING: "The session token is missing.",
    SESSION_VERIFY_FAILED: "We couldn't verify your session.",
    SHOES_LIMIT_RANGE: "The shoe limit must be between 1 and 6.",
    SIGNUP_FAILED: "We couldn't complete the sign-up.",
    SUGGESTIONS_FAILED: "We couldn't build the suggestions.",
    SUITCASE_BUILD_FAILED: "We couldn't pack the suitcase.",
    SUITCASE_DELETE_FAILED: "We couldn't delete the suitcase.",
    SUITCASE_LIST_INVALID: "The suitcase list has an unexpected shape, or is too large.",
    SUITCASE_NOT_OBJECT: "The suitcase must be an object.",
    SUITCASE_READ_FAILED: "We couldn't read the saved suitcase.",
    SUITCASE_SAVE_FAILED: "We couldn't save the suitcase.",
    THUMBNAIL_NEEDS_PHOTO: "The thumbnail changes together with the photo, not on its own.",
    TOO_MANY_GARMENTS: "Up to {max} garments at a time.",
    TOO_MANY_REQUESTS: "Too many requests. Try again in {minutos} min.",
    TRIP_TOO_LONG: "The maximum is {max} days. Longer than that is no longer a suitcase.",
    TRIP_ZERO_DAYS: "The trip has zero days.",
    UNKNOWN_FAMILY: "Unknown colour family: {valor}.",
    UNKNOWN_OCCASION: "Unknown occasion: {valor}.",
    UNKNOWN_REASON: "Unknown reason: {valor}.",
    UNKNOWN_SEASON: "Unknown season: {valor}.",
    UNKNOWN_TEMPERATURE: "Unknown temperature: {valor}.",
    UNSUPPORTED_IMAGE_TYPE: "Unsupported image type. Only {tipos}.",
    VALUE_BAD_FORMAT: "One of the values isn't in the expected format.",
    VETOES_LOAD_FAILED: "We couldn't load your rules.",
    VETO_DELETE_FAILED: "We couldn't delete the rule.",
    VETO_SAVE_FAILED: "We couldn't save the rule.",
    WARDROBE_ANALYSIS_FAILED: "We couldn't analyse your wardrobe.",
    WARDROBE_LOAD_FAILED: "We couldn't load your wardrobe.",
  },

  // ── INTERFAZ ──────────────────────────────────────────────────
  // Las pantallas (2026-08-25): cada clave espeja un t("ns.clave",
  // "respaldo español") del código. El español no vive en es.js:
  // viaja como respaldo dentro del propio t(). test_i18n.js exige
  // paridad de claves y de {marcadores} contra lo que usa src/.
  opciones: {
    orden: {
      recientes: "Newest first",
      carga: "Upload order",
      editadas: "Recently edited",
      nombre: "Name A-Z",
      tipo: "Type",
      combinaciones: "Most combinations",
      usadas: "Recently worn",
      olvidadas: "Longest unworn",
    },
    motivoVeto: {
      color: "The colours clash",
      formalidad: "One is dressier than the other",
      gusto: "Not my style",
      talle: "They don't fit well together",
      otro: "Another reason",
    },
    guardarropa: {
      femenino: "Women's clothing",
      masculino: "Men's clothing",
      ambos: "Both",
    },
    paquete: {
      oficina: "Office work",
      uniforme: "Uniform or workwear",
      casa: "Working from home",
      estudiante: "Studying",
      ninguno: "None of these",
    },
    paqueteDetalle: {
      oficina: "With meetings, clients, or days that ask for more than everyday wear",
      uniforme: "Healthcare, retail, hospitality, trades: work clothes that aren't a formal version of everyday wear",
      casa: "With video calls, which are formal from the waist up",
      estudiante: "Classes, and outings that are neither everyday nor night out",
      ninguno: "Just the core. You create the rest later.",
    },
    nivel: {
      0: "Sport",
      1: "Around the house",
      2: "Out and about",
      3: "Between casual and office",
      4: "Office",
      5: "Night out",
      6: "The most formal",
    },
    toleranciaColor: {
      sobrio: "Understated",
      normal: "Normal",
      arriesgado: "Bold",
    },
    toleranciaFormalidad: {
      estricto: "Strict",
      normal: "Normal",
      relajado: "Relaxed",
    },
  },
  reventon: {
    titulo: "Something broke on our side",
    calma: "Your clothes and your data are safe in your account: this is an error in the app, not in your information.",
    recargar: "Reload the app",
    siSigue: "If it happens again, copy the details below and send them to me.",
    detalle: "Technical details",
  },

  app: {
    listasCaidas: "The lists could not be loaded. What you see may be incomplete.",
    reintentar: "Retry",
    avisoCobroFalla: "Your last payment failed. Check your payment method.",
    avisoPruebaTermina: "Your trial ends on {fecha}.",
    verTuPlan: "See your plan",
    linkVencido: "Link expired",
    linkVencidoExplica: "Recovery links are short-lived and work only once. Request a new one from the login page.",
    salir: "Sign out",
    ayuda: "Help",
    tabArmar: "Build by hand",
    tabArmario: "My wardrobe",
    tabCargar: "Add items",
    tabComprar: "What to buy",
    tabHistorial: "History",
    tabOutfits: "What to wear",
    tabValija: "Suitcase",
    tuPerfil: "Your profile",
    volverLogin: "Back to login",
    guardaste: "Saved {prendas}",
    registrado: "Recorded: {prendas}",
    actualizado: "Entry corrected",
    inventarioPrendas: "Clothes",
    inventarioPerfumes: "Perfumes",
  },
  armador: {
    ampliar: "Enlarge",
    ayer: "Yesterday",
    buscarPorNombre: "Search items by name...",
    buscarPrenda: "Search for an item",
    cargando: "Loading your wardrobe...",
    elegirFecha: "Pick another date",
    guardando: "Saving...",
    limpiar: "Clear",
    meLoPuseHoy: "I wore it today ({n})",
    nadaCoincide: "No items match.",
    otraFecha: "Another date",
    registrar: "Log it",
    sacarPrenda: "Remove {nombre}",
    sinFoto: "No photo",
    todas: "All",
    vaciar: "Clear all",
    volver: "Back",
    editandoDel: "Editing the entry from {fecha}",
    conservadas: "Items no longer in your wardrobe that are kept: {n}",
    elegiUna: "Pick at least one item.",
    fecha: "Date",
    ocasion: "Occasion",
    sinOcasion: "No occasion",
    guardarCambios: "Save changes",
    cancelar: "Cancel",
  },
  armario: {
    marca: "Brand",
    complemento: "Goes on top of any outfit",
    estacion: "Season",
    fichada: "Shown in public",
    fichadaSi: "Already shown",
    fichadaNo: "Not shown yet",
    ampliar: "Enlarge",
    borrar: "Delete",
    borraste: "You deleted",
    buscar: "Search: jeans, shirt, denim, brown...",
    buscarEnArmario: "Search your wardrobe",
    cambiarFoto: "Change photo",
    cargando: "Loading your wardrobe...",
    color: "Color",
    deTotalPrendas: "of {total} items",
    deshacer: "Undo",
    disponibilidad: "Availability",
    editar: "Edit",
    editarPrenda: "Edit {nombre}",
    estado: "Condition",
    estadoConservacion: "Condition",
    filtros: "Filters",
    limpiarBusqueda: "Clear search",
    limpiarFiltros: "Clear filters",
    mostrando: "showing {n} of {total}",
    nadaCoincideCon: "No garment matches \"{q}\".",
    nadaConFiltros: "No items match those filters.",
    noSeRecupero: "We couldn't restore the item: {motivo}",
    ocasion: "Occasion",
    orden: "Sort",
    otros: "Other",
    ponerFoto: "Add photo",
    prendas: "items",
    sinClasificar: "{n} unclassified",
    sinCombis: "Doesn't fit in any combination",
    sinFoto: "No photo",
    sinUsar: "Not worn yet",
    subcategoria: "Subcategory",
    subiendo: "Uploading...",
    tipo: "Type",
    vacio: "Your wardrobe is empty. Upload photos to get started.",
    verMas: "See more",
  },
  calendario: {
    borrada: "deleted",
    borrarRegistro: "Delete entry",
    diaDe: "{mes} {dia}",
    editar: "Edit",
    editarTitulo: "Correct the items, date or occasion of this entry",
    mesAnterior: "Previous month",
    mesSiguiente: "Next month",
    noEntran: ", so they won't be included when you repeat it.",
    registradoHoy: "✓ Logged today",
    repetir: "Repeat",
    repetirTitulo: "Log this same combination for today",
    sinFoto: "no photo",
    vacio: "Nothing to show on the calendar yet. It fills in as you log outfits.",
  },
  carga: {
    estacionesLabel: "Seasons",
    estadoLabel: "Condition",
    marcaPrenda: "Brand (optional)",
    fotoNoCargo: "The photo could not be shown here. The garment saves anyway.",
    fotoBorradorViejo: "This card comes from an earlier batch that did not store a thumbnail. Fixed from the next batch on.",
    yaEnArmario: "Looks like you already have this garment: “{nombre}”. Check before saving.",
    fotoNoVisible: "Photo uploaded (can't be shown until you save)",
    recienGuardadas: "Just saved: {prendas}",
    recuperadas: "Recovered {n} cards from your previous upload.",
    achicando: "Shrinking photos {hechas}/{total}...",
    ampliar: "Enlarge",
    arrastra: "Drag your photos here",
    cambiarColores: "Change colors and occasions",
    colores: "Colors",
    conError: "{n} failed",
    cubre: "Covers {zonas}",
    elegirFotos: "Choose your photos",
    guardando: "Saving...",
    guardarPrendas: "Save {prendas}",
    hastaPorTanda: "Several at once. Up to {max} per batch.",
    iaDudosa: "The AI wasn't sure about this one. Give it a good look.",
    iaRelleno: "I filled in the highlighted fields myself: the AI couldn't read them in the photo. Please check them.",
    iaMirando: "The AI is looking at {hechas}/{total}...",
    iaNoPudo: "The AI couldn't read it. Fill it in by hand.",
    listo: "Done",
    material: "Material?",
    materialLabel: "Material",
    noOcupaZona: "Doesn't take a zone: it's an accessory",
    noSeIntento: "Not attempted: {motivo}",
    noSePudoProcesar: "couldn't be processed",
    nombrePrenda: "Item name",
    ocasiones: "Occasions",
    paraRevisar: "{n} to review",
    procesando: "Processing...",
    queEs: "What exactly it is",
    sacar: "Remove",
    sacarEsta: "Remove this one",
    seTomanPrimeras: "Taking the first {n}: the maximum per batch is {max}.",
    sinEspecificar: "Not specified",
    subiendo: "Uploading...",
    tipoPrenda: "Item type",
    yaTienes: "You already have {n} cards here. Save or remove some before adding more.",
    tomarFoto: "Take a photo",
    quitarTodas: "Remove all",
    quitadas: "{n} cards removed.",
    deshacer: "Undo",
  },
  colores: {
    asiSeVe: "This is how it'll look on the calendar and in each combination.",
    cancelar: "Cancel",
    comoLoDibujo: "How should I draw it when there's no photo?",
    comoSeLleva: "How does it get along with other colors?",
    creando: "Creating...",
    crear: "Create color",
    ejemplos: "Fuchsia, Ecru, Mustard...",
    familia: "Which family should it go in? (for the filter)",
    fuerte: "Bold",
    fuerteExplica: "It draws attention. One per outfit.",
    neutro: "Neutral",
    neutroExplica: "Goes with everything, like black or beige.",
    nombre: "Color name",
    sinMuestra: "Optional. Without a swatch it's drawn with a gray pattern.",
  },
  comprar: {
    masDe: "more than {n}",
    totalAproximado: "Your wardrobe is large and the count stops early: the total is a floor, not an exact figure.",
    ademasRescata: "It also rescues",
    analizando: "Analyzing your wardrobe...",
    comoSeCalcula: "Every number is real: we simulate adding that item to your wardrobe and count again, respecting your vetoes and the style rules. It's not a fashion opinion.",
    deTusCombinaciones: "of your combinations",
    delArmario: "of your wardrobe",
    dependeExplica: "If any of these rips or gets stained, you lose that share of your outfits all at once. These are the ones worth owning twice.",
    dependeTitulo: "What your wardrobe depends on",
    dependeVacio: "No single item concentrates more than 15% of your combinations. You're well spread out.",
    en: "In",
    esElMinimo: "This isn't an upgrade, it's the bare minimum. Everything else on this screen depends on fixing it.",
    faltaCubrirZonas: ": you have nothing to cover {zonas}.",
    frenadaReglas: "Your wardrobe has enough — it was blocked by the style rules or one of your vetoes.",
    gastadasExplica: "Sorted by how much you wear them. A worn-out item holding up half your wardrobe is urgent; one that fits in three outfits you can toss without replacing.",
    gastadasTitulo: "Worn out, in order of urgency",
    gastadasVacio: "You have nothing marked as Worn out.",
    huerfanasExplica: "Each of these either needs a partner or is a candidate to go. That's money already spent.",
    huerfanasTitulo: "Items that don't fit in any combination",
    huerfanasVacio: "Every item you own fits in at least one outfit.",
    leFalta: "It's missing something to cover",
    neutro: "neutral",
    noCompres: "Don't buy anything because of this.",
    noSuma: "It adds nothing: you already have plenty for this zone.",
    otrosColores: "Other colors:",
    para: "For",
    pasarias: "You'd go from {antes} to {despues} combinations ({pct}% more).",
    prendas: "items",
    queConviene: "What's worth buying",
    queHoyNoUsas: "that you don't wear today:",
    recalculando: "Recalculating...",
    restoCuerpo: "the rest of the body",
    sinOutfit: "You can't build a single outfit",
    sinReglasEntraria: "Without rules it would fit in {n}.",
    tipoCalzado: "Shoes",
    tipoEnterizo: "A one-piece or dress",
    tipoInferior: "Something for down below",
    tipoSuperior: "Something for up top",
    todo: "All",
    ver: "see →",
    perfumesTerminan: "Running out",
    perfumeNivel: "{nombre}: {n}% left",
  },
  comun: {
    noSeProceso: "couldn’t be processed",
    y: "and",
    noSeCambioContrasena: "We couldn't change the password",
    sesionVencida: "Your session expired. Please sign in again.",
    sinRed: "No connection to the server. Please try again.",
    formatoNoSoportado: "{archivo} is a {formato} file. Convert it to JPG before uploading.",
    cargando: "Loading...",
    cerrar: "Close",
    guardando: "Saving...",
    unMomento: "One moment...",
    noEsImagen: "{archivo} could not be read as an image.",
    tipoNoAceptado: "{archivo}: only JPG, PNG and WEBP are accepted.",
    noSeConvirtio: "{archivo} could not be converted.",
  },
  editar: {
    marcaEjemplo: "Levis · Zara · Nike",
    cambiar: "Change",
    listo: "Done",
    masNotas: "+ Notes",
    usosAbrir: "Worn when",
    usosCerrar: "Hide when you wore it",
    usosNunca: "You haven't worn it yet. It goes first in the suggestions.",
    usosTope: "Showing the last {cuantas} of {total}",
    marca: "Brand (optional)",
    cancelar: "Cancel",
    colorHuerfano: "This color no longer exists in your lists",
    colorNuevo: "+ New color",
    colores: "Colors",
    disponibilidad: "Availability",
    eligeEstacion: "Pick at least one: an item with no season won't fit in any combination.",
    estaciones: "Seasons",
    estado: "Condition",
    material: "Material",
    nombre: "Name",
    notas: "Notes",
    ocasionHuerfana: "This occasion no longer exists in your lists",
    ocasionNueva: "+ New occasion",
    ocasiones: "Occasions",
    queEs: "What it is",
    sinCambios: "No changes",
    sinEspecificar: "Not specified",
    talla: "Size (optional)",
    tallaEjemplo: "M · 42 · 32x34",
    tipo: "Type",
  },
  elegir: {
    buscar: "Search your wardrobe...",
    cargando: "Loading your wardrobe...",
    sacar: "Remove from the list",
    seMuestran: "Showing the {n} you haven't worn in the longest. Search to see the rest.",
    sinCoincidencias: "No items match that search.",
    sinFoto: "No photo",
    teLlevas: "You're taking {prendas} no matter what",
  },
  ficha: {
    fichada: "Already shown",
    cargando: "Loading details...",
    cerrar: "Close details",
    titulo: "Item details",
  },
  historial: {
    rotoOtro: "This outfit can no longer be put together.",
    rotoNoDisponible: "One of the garments is not available right now.",
    rotoEstaciones: "These garments no longer share any season.",
    rotoZona: "No longer covers: {zonas}. You edited one of the garments.",
    ficharTitulo: "Mark the garments from this outfit as already shown",
    fichar: "Mark as shown",
    borrada: "deleted",
    borrarRegistro: "Delete entry",
    cargando: "Loading your history...",
    guardado: "saved",
    sinFoto: "no photo",
    vacioAntes: "You haven’t logged any outfit yet. When you tap \"I wore this\" in",
    vacioDespues: ", they show up here — and from then on the suggestions start prioritizing what you haven't worn in the longest.",
    vistaCalendario: "Calendar",
    vistaLista: "List",
    editar: "Edit",
    editarTitulo: "Correct the items, date or occasion of this entry",
  },
  inicio: {
    atras: "Back",
    comoEsTuDia: "What's your day like?",
    comoEsTuDiaExplica: "The app uses this to build your occasion list. You can change it later whenever you want.",
    conCualTeVes: "Which one feels most like you?",
    cuantasOcasiones: "{n} occasions",
    despues: "I'll do it later",
    dondeVives: "Where do you live?",
    dondeVivesExplica: "It's the only thing that tells the app what season it is today. In August it's winter in Buenos Aires and summer in Madrid.",
    elTuyo: "Yours",
    eligePais: "Choose your country",
    empezar: "Get started",
    errorOpciones: "We couldn't load the options: {mensaje}",
    estiloExplica: "Pick the one that represents you most. The app uses it to sort suggestions — none are ruled out.",
    guardando: "Saving...",
    notaSaltear: "If you skip this, the app starts with six generic occasions and no idea which hemisphere you're in. Everything can be set up later in Profile.",
    queRopa: "What clothes do you wear?",
    queRopaExplica: "It sorts the lists: what you wear shows up first. Nothing is hidden — you can still add any item.",
    seguir: "Next",
    sumaAcentos: "If you want, add up to two more that also represent you.",
    tambien: "Also",
    pasoDeN: "Step {n} of {total}",
  },
  listas: {
    queSonOcasiones: "Occasions are what let the app know which garments go together. Pick the one closest to your day and I'll build a starter list; you can change it later.",
    armaTuLista: "Build your occasion list",
    borrar: "Delete",
    vetosTitulo: "Combinations you marked as not working",
    vetoNota: "When you remove one, those two items can appear together again in your suggestions.",
    prendaBorrada: "(deleted)",
    prendaDesconocida: "An item that is no longer there",
    veto: "mark",
    borrarIgual: "Delete anyway",
    botonColorNuevo: "+ New color",
    botonOcasionNueva: "+ New occasion",
    cancelar: "Cancel",
    cargando: "Loading your lists...",
    color: "color",
    coloresCreados: "Colors you created",
    formalidad: "formality {n}",
    neutro: "neutral",
    notaBorrado: "Items that already had it keep showing it with ⚠, but lose it the next time you save that item.",
    ocasion: "occasion",
    ocasionesCreadas: "Occasions you created",
    sinMuestra: "no swatch",
    tusOcasiones: "Your occasions",
    vacio: "You haven't created any colors or occasions of your own yet. They're created while editing an item, with the buttons",
    y: "and",
    sembrarConserva: "If you already added items, the occasions those items use are kept. Nothing is lost.",
    borrarTitulo: "Delete this {que}",
  },
  login: {
    avisoConfirmar: "We sent an email to {email}. Confirm your account and then sign in.",
    avisoReset: "If there's an account for {email}, you'll get an email with a link to change your password.",
    comoTeLlamas: "What's your name",
    crearBoton: "Create account",
    crearCuenta: "Create your account",
    email: "Email",
    entrar: "Sign in",
    entrarTitulo: "Sign in to your wardrobe",
    faltaEmail: "Enter your email",
    faltaPassword: "Enter your password",
    idioma: "Language",
    mandarLink: "Send me the link",
    minimo8: "At least 8 characters",
    nombre: "Name",
    olvide: "I forgot my password",
    olvideExplica: "Enter your email and we'll send you a link to set a new password.",
    password: "Password",
    passwordCorta: "The password must be at least 8 characters",
    recuperar: "Recover password",
    registrate: "No account? Sign up",
    volverEntrar: "Back to sign in",
    yaTengoCuenta: "Already have an account? Sign in",
  },
  ocasiones: {
    como: "like {ocasion}",
    cancelar: "Cancel",
    creando: "Creating...",
    crear: "Create occasion",
    ejemplos: "Church, Barbecue, Long trip...",
    nombre: "Occasion name",
    paraQue: "This tells the app which items can go together. An office occasion doesn't mix with gym clothes.",
    queTanFormal: "How formal is it? (so things match well)",
  },
  outfits: {
    ningunaCoincide: "None of your {n} garments matches \"{texto}\".",
    sinAlternativas: "You have no other garment that fills that spot. It’s the same thing \"what to buy\" says: that part of your wardrobe depends on a single garment.",
    sinCoincidenciasLugar: "None of the options for that spot matches \"{busqueda}\". The garment may exist in your wardrobe but not cover the same part of the body.",
    sinMostrar: "Not shown",
    sinMostrarTitulo: "Only pair clothes you haven't shown in public yet",
    noSacarUltima: "At least one garment must stay",
    quitar: "Remove {nombre}",
    activarReglas: "Turn rules on",
    alternativasConteo: "{opciones} · least recently worn first",
    ampliar: "Enlarge",
    anclaNoCoincide: "It doesn't match {motivos}. It's shown anyway because you picked it.",
    anteayer: "Two days ago",
    ayer: "Yesterday",
    ayudaEditar: "Tap the item you want to swap. You can swap one, two or all three.",
    borrarBusqueda: "Clear search",
    buscando: "Searching...",
    buscarAlternativas: "Search these options...",
    buscarPrendaCombinar: "Search for an item to pair",
    buscarReemplazo: "Search for a replacement item",
    cambiar: "Swap {nombre}",
    cargaPais: " Set your country in your profile and it'll pick itself.",
    cargandoArmario: "Loading the wardrobe...",
    cargandoTuArmario: "Loading your wardrobe...",
    combinacionesCon: "Combinations with",
    combinarPrenda: "Pair an item...",
    conEstosFiltros: " with these filters",
    conFueraDeEstacion: "There wasn't enough in-season clothing for this weather, so there are spring-summer items among the suggestions.",
    conGastadas: "To build something for this occasion, worn-out items had to be included. They're marked.",
    conectorY: " and ",
    cortoDeAbrigo: "Might not be warm enough",
    deTotal: " · of ",
    descartadas: "Combinations were discarded {partes}.",
    distintas: "different",
    editadoDeshacer: "Edited · undo",
    editar: "Edit",
    enPantalla: "on screen",
    estacionHoy: "It's today's season where you are",
    estanMarcadas: "They're marked.",
    faltaAyudaCarga: "Add one of those and the combinations will show up.",
    faltaAyudaFiltros: "Try removing a filter, or tag more items with this occasion from your wardrobe.",
    gastada: " worn out",
    hoy: "Today",
    limpiarBusqueda: "Clear search",
    listo: "Done",
    meLoPuse: "I wore it",
    niCon: " or ",
    ningunaConFiltros: "No combinations with these filters.",
    noMeGusta: "I don't like this combination",
    noVa: "Doesn't match",
    ocasion: "Occasion",
    otrasOpciones: "Other options",
    paraSumar: "To layer on top (not counted in the mix)",
    porColor: "by color",
    porFormalidad: "by formality",
    porQueNoVa: "— why doesn't it work?",
    porVetos: "by your vetoes",
    prefieroNoDecir: "I'd rather not say",
    quePar: "Which two items don't go together?",
    registrado: "✓ Logged",
    registrar: "Log it",
    reglasActivas: "Style rules on.",
    reglasApagadas: "Style rules off: showing everything that covers the body.",
    sinAbrigoAlgunas: "{n} of these combinations ended up without outerwear.",
    sinAbrigoPegue: "No outerwear that goes with this",
    sinAbrigoTodas: "You don't have any outerwear that goes with these items and works for this cold.",
    sinEstacion: "No season selected: showing combinations from your whole wardrobe.",
    sinFoto: "No photo",
    sinRopa: "You haven't added any clothes yet.",
    sinRopaAyuda: "Take a photo of what you have and the app classifies it on its own. With one top, one bottom and one pair of shoes, it starts suggesting outfits.",
    teFalta: "You're missing",
    tituloEditar: "Replace items in this combination",
    todo: "All",
    vacioPorReglas: "Your wardrobe has enough, but the style rules discarded everything.",
    vacioPorReglasAyuda: "It usually happens with a very specific occasion. Try \"Show all equally\" up here to see the raw list.",
    verTodasCombinaciones: "See all combinations",
    verTodasIgual: "Show all anyway",
    vestirsePara: "Dressing for {estacion}",
    vetosNoLeidos: " (we couldn't read your vetoes)",
    xDeY: "{visibles} of {total}",
    yaViste: "you've seen",
    yaVisteTodas: "You've seen every combination with these filters. Change a filter to see more.",
    zonaPiernas: "something for down below",
    zonaPies: "a pair of shoes",
    zonaTorso: "something for up top",
  },
  perfil: {
    borrarCuenta: "Delete my account",
    borrarCuentaExplica: "Everything goes: your garments, your photos, your outfits and your account. This cannot be undone.",
    borrarCuentaSeguro: "Delete your account?",
    borrarCuentaQue: "This deletes, right now and forever: your garments, all your photos, your logged outfits, your own colors and occasions, your suitcase and your account.",
    borrarCuentaSinVuelta: "It cannot be undone and there is no backup.",
    cancelar: "Cancel",
    borrandoCuenta: "Deleting...",
    borrarCuentaConfirmar: "Yes, delete everything",
    tuPlan: "Your plan",
    planVencido: "Your plan has ended",
    planPrueba: "Free trial",
    planMensual: "Monthly subscription",
    planAnual: "Annual subscription",
    planCortesia: "Complimentary access",
    planTerminoEl: "Ended on {fecha}",
    planTermina: "Ends on {fecha}",
    planRenueva: "Renews on {fecha}",
    planPrendas: "{n} of {max} garments",
    planSinTope: "No garment limit",
    planVencidoQue: "You keep seeing, editing, deleting and logging your wardrobe. To add new garments you need a subscription.",
    planCobroFalla: "Your last payment failed. Check your payment method.",
    planProximamente: "Subscribe (coming soon)",
    estacionHoy: "It’s {estacion} where you are: \"What should I wear\" starts in that season.",
    paisXN: "Other (northern hemisphere)",
    paisXS: "Other (southern hemisphere)",
    paisXT: "Other (tropical climate)",
    idiomaSinTraducir: "This language isn't translated yet: the app shows in English, and whatever is missing, in Spanish.",
    idiomaCompleto: "The app is complete in this language. The translation hasn't been reviewed by a native speaker yet.",
    idiomaEsCompleto: "The app is complete in Spanish.",
    acentos: "Accents",
    acentosNota: "(up to 2, optional)",
    cambiarNombre: "Change name",
    coloresNoUsas: "Colors you don't wear",
    coloresNoUsasDetalle: "Items in these colors won't show up in suggestions, and I won't recommend buying them. It's different from a veto: a veto is about one pair of items, this is about the whole color.",
    comoSugiero: "How I suggest your clothes",
    comoSugieroDetalle: "This changes the combinations you see. It's the highest-impact thing on this screen.",
    comoTeLlamo: "What should I call you?",
    dondeVives: "Where you live",
    estiloDominante: "Main style",
    guardadoOk: "Saved.",
    guardando: "Saving...",
    guardarPreferencias: "Save preferences",
    idioma: "Language",
    idiomaInterfaz: "Interface language",
    mezclaColores: "Color mixing",
    mezclaFormalidad: "Formality mixing",
    misListas: "My lists",
    ordenNeutro: "Lists in neutral order",
    pais: "Country",
    paraQueElPais: "It lets the app know what season it is today and start on the right one.",
    paraQueVistes: "What you usually dress for",
    paraQueVistesDetalle: "\"What to buy\" uses it. Without it I guess by looking at your wardrobe, and that’s circular: if you lack work clothes, your wardrobe says you don’t work and I never suggest buying any.",
    prefieroNoDecir: "I'd rather not say",
    queRopaUsas: "What clothes you wear",
    queRopaUsasDetalle: "It sorts the lists: what you wear shows up first. Nothing is hidden — you can still add any item.",
    sinDefinir: "Not set",
    sinEspecificar: "Not specified",
    sinEstaciones: "Your country doesn't have four distinct seasons, so you pick the season yourself each time.",
    sinNombre: "No name",
    sinOrdenEstilo: "Suggestions aren't sorted by style",
    tuEstilo: "Your style",
    tuEstiloDetalle: "It sorts the 'What to wear' suggestions: whatever speaks your style most shows up first. It never hides anything.",
    tuGuardarropa: "Your wardrobe",
    tuNombre: "Your name",
    version: "Version",
  },
  portada: {
    eslogan: "You already own it.",
    titular: "You photograph your clothes once — Brujel builds your outfits from all of them, not just the three you always reach for.",
    contexto: "Your wardrobe is an asset you don't manage. Forty items, and you wear three. The other thirty-seven aren't lost or worn out — they're invisible. You don't see them when you open the door at seven in the morning, so you buy a shirt you already had, and one that cost the same dies with the tag still on it.",
    pruebasTitulo: "Everything below is built and running",
    p1: "One photo per item",
    p1d: "Upload them in a batch. Brujel reads the garment, the brand, the colours and the occasions straight from the photo.",
    p2: "It builds the outfits",
    p2d: "Filtered by occasion, and ordered by what you haven't worn in the longest.",
    p3: "From your whole wardrobe",
    p3d: "A shirt worn forty times but not in two months beats one you wore twice on Wednesday.",
    p4: "And it pushes you not to buy",
    p4d: "“What to buy” has five sections and the buying ones come last. Four of the five are solved without spending anything.",
    pieNota: "Your wardrobe, in numbers. No fashion horoscopes.",
  },
  reset: {
    cambiar: "Change password",
    cancelar: "Cancel",
    guardando: "Saving...",
    irLogin: "Go to login",
    listoTexto: "Your password has been changed. You can now sign in with the new one.",
    listoTitulo: "Done",
    minimo8: "At least 8 characters",
    mismaOtraVez: "The same one again",
    noCoinciden: "The two passwords don't match",
    passwordCorta: "The password must be at least 8 characters",
    passwordNueva: "New password",
    repetirla: "Repeat it",
    subtitulo: "Choose a password for your account",
    titulo: "New password",
  },
  valija: {
    noSeBorroEnLaCuenta: "Deleted here, but not in your account: it may come back on another device. Please try again. ({motivo})",
    unDiaMas: "One day more of {ocasion}",
    unDiaMenos: "One day less of {ocasion}",
    acaHoy: "(here, today)",
    ahorro: "A different outfit per day would be {sinOptimizar}. You save {ahorro}.",
    armando: "Packing...",
    calzados: "Shoes",
    cualquiera: "Any",
    cuantosDias: "How many days of each?",
    dia: "Day {n}",
    errorLeer: "We couldn't read the saved suitcase: {mensaje}",
    estacion: "Season",
    hasta: "up to {n}",
    laElegiste: "you picked it",
    listo: "Done",
    nueva: "New suitcase (start from scratch)",
    ocultarPlan: "Hide the plan",
    paraMeter: "To toss in the bag",
    prendasPara: "items for {dias} days",
    quieresLlevar: "Anything you want to take no matter what?",
    sinFoto: "No photo",
    sinResolver: "Unresolved",
    sumaUnDia: "Add at least one day",
    teLlevas: "You're taking {n} no matter what — change",
    usoDias: "{dias} of {total} days",
    verPlan: "See day by day",
  },
  visor: {
    fotoAmpliada: "Enlarged photo",
    sinUsar: "Not worn yet",
    ultimaVez: "Last worn: {cuando}",
    verFicha: "View item details",
  },

  version: {
    noSePudoLeer: "couldn't be read",
    servidorYTuyo: "Server: {servidor} · Yours: {tuyo}",
    copiaVieja: "Your browser has an old copy. Refresh with Ctrl+Shift+R.",
    alDia: "You're on the latest deployed version.",
    modoDesarrollo: "Development mode: the code is served by react-scripts.",
    arranco: "The server started {cuando}.",
  },

  ayuda: {

    perfil5: "At the very bottom is «Delete my account». It deletes for real and right away: your garments, all your photos, your outfits, your own colors and occasions, your suitcase and the account itself. No copy is kept and it cannot be undone, so it asks you twice.",
    armar1: "To record an outfit you put together yourself, without the app suggesting it. Search the items by name, add them, and record with \"I wore it today\", \"Yesterday\" or another date. When you record, the wardrobe and the history update and you move to the history to see the result.",
    armarTitulo: "Build by hand",
    armario1: "All your items. You can search by name, notes, colour, material, type, occasion or brand, and filter by condition, availability, colour, occasion, season, type, subcategory, brand, no photo, not worn yet, or whether you have shown it in public. Each filter shows how many items it has; if it has none, it does not appear. At the top you can change the order.",
    armario2: "Each card shows the brand if known, when you last wore it, how many times, and how many outfits it fits in. \"Doesn't fit in any outfit\" is the most useful signal: that item is not working for you. A coat or accessory says \"Goes on top of any outfit\" because it does not take another item's place. From the card you change the photo, open the card to edit, or delete; if you delete by mistake, you can undo it right away.",
    armarioTitulo: "My wardrobe",
    cargar1: "Drag or choose several photos at once, or take one with your phone's camera, up to a maximum per batch. Any card can be removed, and “Remove all” clears the batch with a few seconds to undo. The app shrinks them on your phone, recognises them and gives you back one card per photo to review: name, brand, type, what it is, season, material, condition, colours and occasions. If it was not sure, it tells you; if it could not see it, you fill it in by hand. If it couldn't read a field from the photo, it fills it with a default and highlights it, so you check those and not all twelve.",
    cargar2: "If a photo looks like an item you already have, it tells you which one before saving; it does not stop you, because two identical white t-shirts are two items. When you save, you stay on this screen with the just-saved items, ready for the next batch. If you close the app halfway through a batch, you get the cards back when you return.",
    cargar3: "When you create your account you get a {dias}-day trial, with no card and up to {tope} garments, to see how everything works. When the trial ends nothing is deleted: you keep seeing your whole wardrobe, editing the cards, deleting what you don’t wear, logging what you put on and getting suggestions. The only thing that needs a subscription is adding new garments — and with one there is no limit.",
    cargarTitulo: "Add items",
    comprar1: "Five readings of your wardrobe. If you cannot put together any outfit, it tells you what you are missing to cover your body: that comes first. The items that do not fit in any outfit, which need a partner or are candidates to go. Which items your wardrobe depends on: those in more than 15% of your outfits, worth having in duplicate. And the worn-out ones, ordered by urgency according to how much you wear them.",
    comprar2: "And what is worth buying: every number is real. The app simulates adding that item to your wardrobe and counts your outfits again, respecting your vetoes, your style rules and the colours you do not wear. It is not an opinion about fashion.",
    comprarTitulo: "What to buy",
    ficha1: "The fields are ordered by what changes most: first availability and condition (sending something to the laundry is the most common), then occasions, colours, type and what exactly it is, seasons, name, material, size, brand and notes. Colours and occasions show only what is selected: a tap on one removes it, and \"Change\" opens the full list to add more.",
    ficha2: "The name you gave it is never overwritten. The brand is suggested by the app by reading the logo in the photo, and you confirm it; if you type \"tommy h\" and already have \"Tommy Hilfiger\", they are grouped together. \"Item shown\" marks that you have already worn it in public and is saved instantly, without closing the card. Only what you changed is saved.",
    ficha3: "At the very bottom, «Worn when» opens the list of dates this garment went out, with the occasion for each one. It's read-only: if you got a day wrong when you logged it, fix it from History.",
    fichaTitulo: "An item's card",
    historial1: "Everything you recorded, as a list or a calendar. If you later edited an item and the outfit can no longer be put together (it stopped covering a zone, the items no longer share a season, or one is unavailable), the history tells you but does not change it: what you wore, you wore. From each record you can mark its items as shown, or delete the record.",
    historial2: "In the calendar, each day opens what you wore. \"Repeat\" logs that same outfit with today's date. \"Edit\" (also in the list) opens the entry in Build by hand to correct its items, its date or its occasion; saving corrects that same entry, it doesn't create a new one.",
    historialTitulo: "History and calendar",
    indice: "Sections",
    inicio1: "Brujel suggests what to wear from the clothes you already own. You take a photo of each item and the app recognises it on its own: what it is, its colours, the material, which occasions it suits, which seasons, and the brand if it can be seen. With one top, one bottom and one pair of shoes it starts suggesting outfits.",
    inicio2: "The first time, it asks you four questions. Where you live, to know what season it is today (August is winter in Buenos Aires and summer in Madrid). What clothes you wear, to show you first what you wear most; nothing is hidden. What your day is like, to build your list of occasions, which you can change whenever you want. And which style you identify with, to order the suggestions; none is discarded.",
    inicioTitulo: "Getting started",
    intro: "What each screen does and how Brujel decides what to suggest.",
    outfits1: "This is the everyday screen. At the top you choose which season you are dressing for (it starts on today's, based on your country), the occasion, and whether to combine only clothes you have not shown in public yet. With \"Combine an item\" you pick one item and see only outfits that include it, even if it does not meet the filters; the app tells you which filter it is letting through.",
    outfits2: "Before combining, the app sets apart the items that can take part: the available ones (not in the laundry or lent out), those that suit the chosen occasion and season, and it leaves out worn-out items when the occasion calls for it. If that is not enough to dress you, it brings back the worn-out or off-season clothes, and lets you know.",
    outfits3: "Every outfit has something for the top, something for the bottom and shoes (a dress or one-piece covers two). Coats and accessories are offered separately, to add on top, and depending on the season each outfit comes with a matching coat: a light one in spring and autumn, a proper one in winter, none in summer. Three rules discard outfits: colour (not too many strong colours together; a single patterned item with the rest neutral does pass), formality (a gym t-shirt and a blazer do not go together on any occasion) and your vetoes (\"not this with that\"). \"Show all the same\" turns off the colour and formality rules; your vetoes are always respected.",
    outfits4: "Suggestions come ordered by four criteria, from the most important to the tie-breaker. First, how long since you wore those items: never worn, more than a month ago, between a week and a month, this week. Second, outfits with clothes you have not shown in public yet come first. Third, how many times you wore them: never, barely worn (fewer than 3), normal use (fewer than 12), everyday. Fourth, your style: among similar outfits, the one closest to your style wins; it only orders, never discards. Whatever is still tied is drawn at random, and \"Other options\" draws again.",
    outfits5: "You see 18 suggestions at a time, chosen so the same items are not repeated, and in the order above. In each one you can \"Edit\" to change one, two or all three items; mark \"Doesn't match\" and say which two items do not go together (it is kept as a veto); or record \"I wore it\" today, yesterday or the day before. Recording what you wear feeds your history and makes the suggestions prioritise what you have not worn for the longest.",
    outfitsTitulo: "What to wear",
    perfil1: "Opens by tapping your name at the top. \"How I suggest clothes\" is what changes the suggestions most: how much mixing of colours and formality you accept, and the colours you do not wear; those items do not appear in the suggestions and are not recommended for purchase. Where you live decides today's season. Your style and up to two accents order the suggestions without hiding anything; over time, what you record fine-tunes what you declared.",
    perfil2: "In «My lists» you create your own occasions and colours with «+ New occasion» and «+ New colour», and you can also create them while editing a garment, which marks it there straight away. That's where you see the ones you already made and the combinations you ruled out; deleting one doesn't affect the garments that already had it. You also change the app's language there (there are twelve) and see which version you're on.",
    perfil3: "You can also pick the language before signing in, with the selector at the top of the entry screen: all twelve appear with their name in their own language. The app remembers the one you chose in that browser, so next time it greets you in the same one. And if you sign up, your account is created in that language.",
    perfil4: "At the very bottom is «Your plan»: which one you have, until when, and how many garments you have out of the limit if there is one. When your trial has only a few days left, or if a payment fails, a notice also appears above every screen; with the plan already ended it does not, because the limit itself tells you when you try to add a garment.",
    perfilTitulo: "Your profile",
    titulo: "Help",
    valija1: "You say how many days of each occasion the trip has, for which season, how many pairs of shoes at most, and whether there is something you must take. The app builds the shortest list of items that covers every day, shows you how much you save compared to a different outfit per day, and the day-by-day plan. The suitcase stays saved; \"New suitcase\" starts from scratch.",
    valijaTitulo: "Suitcase",
    perfumesTitulo: "Perfumes",
    perfumes1: "In My wardrobe, at the top, you switch between Clothes and Perfumes. To add a perfume, type its name and tap “Fill in with AI”: it suggests the brand, the concentration (cologne, eau de toilette, eau de parfum or parfum), the olfactory family and whether it's for day, night or both. What it suggested stays highlighted until you review it. The AI answers from what it knows, without searching the internet: if it doesn't recognize the perfume it tells you so and you fill in the details yourself. It never changes what you already typed.",
    perfumes2: "On each card, move the slider to say, roughly, how much is left in the bottle. With the opening date it warns you at three years, when a perfume can start to change, and again at five. The photo is your own, of the bottle. From the card you can also edit or delete it; if you delete it by mistake, you can undo it right away. Older entries keep showing that perfume.",
    perfumes3: "When you tap “I wore it”, in What to wear or Build by hand, a bottle appears with the last perfume you used (or the last one you added): tap it to change it or choose “No perfume”. Each day in History shows the outfit and the bottle, and you can correct it from “Edit”. When a perfume has 15% or less left, What to buy tells you it's running out.",
  },
  frasco: {
    titulo: "The perfume you wore",
    sinPerfume: "No perfume",
    elegir: "Choose perfume",
    conPerfume: "Perfume: {nombre}",
  },
  perfumes: {
    cargando: "Loading your perfumes...",
    borrado: "You deleted {nombre}.",
    deshacer: "Undo",
    agregar: "Add perfume",
    vacio: "You haven't added any perfume yet. Add the first one by its name: the AI suggests the brand, the concentration and the family, and you confirm.",
    subiendo: "Uploading...",
    cambiarFoto: "Change photo",
    ponerFoto: "Add photo",
    sinUsar: "Not used yet",
    nivel: "Left: {n}%",
    nivelEtiqueta: "How much is left in the bottle",
    seTermina: "It's running out.",
    avisoCinco: "Opened more than five years ago: it has probably changed its scent.",
    avisoTres: "Opened more than three years ago: it may start to degrade.",
    editar: "Edit",
    borrar: "Delete",
    faltaNombre: "Type the name first.",
    iaNoConoce: "The AI doesn't recognize this perfume with certainty. Fill in the details yourself.",
    iaPropuso: "The AI filled in the highlighted fields. Review them before saving.",
    iaNadaNuevo: "The AI had nothing to add to what you already typed.",
    sinDato: "Not set",
    editando: "Edit perfume",
    nuevo: "New perfume",
    nombre: "Name",
    nombrePista: "For example: Sauvage",
    marca: "Brand",
    completando: "Checking...",
    completarIA: "Fill in with AI",
    etiquetaConcentracion: "Concentration",
    etiquetaFamilia: "Olfactory family",
    etiquetaMomento: "Time of day",
    ml: "Bottle size (ml)",
    abiertoEn: "Opening date",
    guardando: "Saving...",
    guardar: "Save",
    cancelar: "Cancel",
    concentracion: {colonia: "Cologne", edt: "Eau de toilette", edp: "Eau de parfum", parfum: "Parfum / extrait"},
    familia: {citrico: "Citrus", fresco: "Fresh / aquatic", aromatico: "Aromatic / fougère", amaderado: "Woody", ambar: "Amber / oriental", floral: "Floral", chipre: "Chypre", gourmand: "Gourmand", cuero: "Leather"},
    momento: {dia: "Day", noche: "Night", ambos: "Day and night"},
  },
  plural: {
    prendas: { one: "{n} item", other: "{n} items" },
    combinaciones: { one: "{n} combination", other: "{n} combinations" },
    outfits: { one: "{n} outfit", other: "{n} outfits" },
    listas: { one: "{n} list", other: "{n} lists" },
    veces: {one: "{n} time", other: "{n} times"},
    cambios: {one: "Save {n} change", other: "Save {n} changes"},
    opciones: {one: "{n} option", other: "{n} options"},
    diasConRegistro: {one: "{registrados} of {n} day with a record", other: "{registrados} of {n} days with records"},
    prendasFaltan: {one: "{n} item from this outfit is no longer in your wardrobe", other: "{n} items from this outfit are no longer in your wardrobe"},
    outfitsRegistrados: {one: "outfit recorded", other: "outfits recorded"},
    diasValija: {one: "Pack for {n} day", other: "Pack for {n} days"},
    diasSinResolver: {one: "{n} day unresolved.", other: "{n} days unresolved."},
  },
};

export default en;
